import "./dashboard.page.css"
import useAnimateIn from "../../hooks/useAnimateIn"
import { useEffect, useState } from "react"
import API from "../../api"
import SkeletonComponent from "../../components/skeleton/skeleton.component"
import moment from "moment"
import { Link, useNavigate } from "react-router-dom"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"

import { randomColor } from "randomcolor"

const DashboardPage = (props) => {
  const { user, userMeta } = props
  const [loading, setLoading] = useState(true)
  const [tests, setTests] = useState([])
  const [filteredTests, setFilteredTests] = useState([])
  const [search, setSearch] = useState("")
  const [chartData, setChartData] = useState()
  const navigate = useNavigate()

  // Fetch on load
  useEffect(() => {
    const fetchTests = async () => {
      try {
        const testsRes = await API.makeGetRequest(user, "tests")
        const limitedTests = testsRes.slice(0, Math.min(5, testsRes.length))
        setTests(testsRes)
        setFilteredTests(limitedTests)

        // Make status + trial buckets
        const statuses = {}
        const trials = {}
        testsRes.forEach((t) => {
          if (!statuses[t.status]) {
            const label =
              t.status === "Ready"
                ? "Tests Completed"
                : t.status === "In Progress"
                ? "Tests in Progress"
                : "Tests Ordered"
            const color =
              t.status === "Ready"
                ? "#14002F"
                : t.status === "In Progress"
                ? "#BEB3CF"
                : "#684D8E"
            statuses[t.status] = { label, count: 0, color }
          }
          statuses[t.status].count += 1

          if (!trials[t.trialId]) {
            trials[t.trialId] = {
              label: t.trialId,
              count: 0,
              color: randomColor({ hue: "purple" }),
            }
          }

          trials[t.trialId].count += 1
        })

        // Convert trials to percentages
        const trialVals = []
        Object.values(trials).forEach((t) => {
          trialVals.push({
            ...t,
            count: Math.round((t.count / testsRes.length) * 100),
          })
        })

        setChartData({
          status: Object.values(statuses),
          trials: trialVals.sort((a, b) => (a.label > b.label ? 1 : -1)),
        })
      } catch {
        // todo handle error
      } finally {
        setLoading(false)
      }
    }

    if (user) {
      fetchTests()
    }
  }, [user])

  const handleSearch = (val) => {
    setSearch(val)

    // Search
    if (val.length > 0) {
      setFilteredTests(
        [...tests].filter((t) => {
          const v = val.toLowerCase()
          return (
            t.id.toLowerCase().indexOf(v) > -1 ||
            t.patientId.toLowerCase().indexOf(v) > -1 ||
            t.trialId.toLowerCase().indexOf(v) > -1
          )
        })
      )
    } else {
      const limitedTests = [...tests].slice(0, Math.min(5, tests.length))
      setFilteredTests(limitedTests)
    }
  }

  const handleStatusClick = (t) => {
    if (t.status === "Ready") {
      navigate(`/tests/${t.id}`)
    }
  }

  useAnimateIn()

  return (
    <div className="cb-page cb-dashboard">
      <div className="cb-page-header">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>
      <div className="cb-page-content">
        <h2>Hello {userMeta.fullName}</h2>
        <h5>Here's your analytic details</h5>
        <div className="cb-list-view">
          <div className="cb-list-view-header">
            <h4>Latest updates</h4>
            <h6>
              Updated at{" "}
              {moment().subtract(30, "min").format("MM/DD/YYYY hh:mm a")}
            </h6>
          </div>
          {loading && <SkeletonComponent />}
          <ul className="cb-list">
            <li className="cb-list-row row-header">
              <div>Test ID</div>
              <div>Patient ID</div>
              <div>Trial</div>
              <div>Date Ordered</div>
              <div>Status</div>
              <div>Last update</div>
            </li>
            {filteredTests?.map((t) => {
              return (
                <li className="cb-list-row" key={t.id}>
                  <div>
                    <Link to={`/tests/${t.id}`}>{t.id}</Link>
                  </div>
                  <div>{t.patientId}</div>
                  <div>{t.trialId}</div>
                  <div>{moment(t.orderDate).format("MM/DD/YYYY")}</div>
                  <div>
                    <span
                      onClick={() => handleStatusClick(t)}
                      className={`cb-status-tag ${t.status
                        .replace(" ", "-")
                        .toLowerCase()}`}
                    >
                      {t.status}
                    </span>
                  </div>
                  <div>
                    {t.resultDate
                      ? moment(t.resultDate).format("MM/DD/YYYY")
                      : moment(t.orderDate).format("MM/DD/YYYY")}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
        {tests.length > 10 &&
          tests.length !== filteredTests.length &&
          !search && (
            <div className="cb-list-actions">
              <Link to="/tests" className="cb-list-see-all-btn">
                See all
              </Link>
            </div>
          )}

        {/* Charts */}
        {chartData &&
          chartData.status &&
          chartData.trials &&
          chartData.trials.length > 0 && (
            <div className="cb-dash-charts">
              <div className="cb-dash-chart">
                <h4>Test Status Summary</h4>
                <div className="cb-dash-chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData.status}
                      margin={{
                        top: 20,
                        right: 20,
                        left: -30,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      <YAxis />
                      <Bar dataKey="count">
                        {chartData.status.map((entry) => (
                          <Cell
                            key={entry.label}
                            fill={entry.color}
                            opacity={
                              entry.label === "Tests in Progress" ? 0.4 : 0.6
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="cb-dash-chart">
                <h4>Clinical Trial Summary</h4>
                <div className="cb-dash-chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData.trials}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="count"
                        label={(entry) => {
                          return `${entry.payload.count}%`
                        }}
                      >
                        {chartData.trials.map((entry) => (
                          <Cell key={entry.label} fill={entry.color} label />
                        ))}
                      </Pie>
                      <Legend
                        formatter={(_, entry) => {
                          return entry.payload.payload.payload.label
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  )
}

export default DashboardPage
